import { FrameEmptyState } from "@/components/shapes/shared/frame-empty-state";
import { useState } from "react";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Spinner } from "@/components/ui";
import { useFrameSurface } from "../shared/use-frame-surface";
import { useT } from "@/i18n";
import { previewCardFrameMessages } from "@/i18n/messages/frames-preview-card";

/**
 * Production PreviewCardFrame — read-only rich preview card (hero image +
 * caption bar).
 *
 * Layout copied from the workshop preview-card-frame. Offline-safe: the
 * hero <img> sits on a gradient fallback tile, so a failed load still
 * renders a composed card. No FrameHeader/FrameFooter: canvas drag uses
 * the hover chrome drag zone, and follow-ups/resize/delete come free
 * via the wrap() HOC chain + hover chrome. Content renders from
 * data.content; agent frame.update broadcasts re-render automatically.
 */
export function PreviewCardFrame({ id, data, selected }: FrameProps) {
  const t = useT(previewCardFrameMessages);
  const frameSurface = useFrameSurface(id);
  const content = data.content ?? {};
  const imageUrl = content.imageUrl as string | undefined;
  const title = (content.title as string) ?? "";
  const source = (content.source as string) ?? "";
  const status = data.status;

  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const showImage = Boolean(imageUrl) && failedImageUrl !== imageUrl;
  const hasContent = Boolean(imageUrl || title || source);

  return (
    <div
      className={cn(
        frameSurface.className,
        "group relative flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="preview-card-frame"
      {...frameSurface.attributes}
    >
      <div
        className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 [--denk-frame-content-padding-block-start:16px] [--denk-frame-content-padding-block-end:16px] [--denk-frame-content-padding-inline:16px]"
        data-frame-content="true"
        data-frame-drag-handle="true"
        onClick={(e) => e.stopPropagation()}
      >
        {status === "streaming" && !hasContent ? (
          <div
            className="flex items-center gap-2 px-3 py-4"
            data-testid="preview-card-frame-loading"
            aria-label={t("loadingPreview")}
          >
            <Spinner className="h-3 w-3" />
            <span className="text-appkit-body text-muted">
              {t("loadingPreviewProgress")}
            </span>
          </div>
        ) : !hasContent ? (
          <FrameEmptyState
            frameId={id}
            status={status}
            testId="preview-card-frame-empty"
          >
            {t("noPreview")}
          </FrameEmptyState>
        ) : (
          <>
            {imageUrl && (
              <div
                className="relative aspect-[16/10] w-full overflow-hidden rounded-frame bg-elevated bg-gradient-to-br via-elevated to-elevated"
                data-testid="preview-card-frame-hero"
              >
                {showImage ? (
                  <img
                    src={imageUrl}
                    alt={title}
                    loading="lazy"
                    onError={() => setFailedImageUrl(imageUrl ?? null)}
                    className="h-full w-full object-cover"
                    data-testid="preview-card-frame-hero-image"
                  />
                ) : (
                  <div
                    className="flex h-full w-full items-center justify-center"
                    data-testid="preview-card-frame-hero-fallback"
                    aria-label={title}
                  >
                    <Icons.Image
                      className="h-8 w-8 text-muted"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>
            )}

            <div
              className="flex items-center gap-2 p-4"
              data-testid="preview-card-frame-caption"
            >
              <div className="min-w-0 flex-1">
                <p
                  className="truncate text-appkit-body font-semibold leading-snug text-primary"
                  data-testid="preview-card-frame-title"
                >
                  {title}
                </p>
                {source && (
                  <p
                    className="mt-0.5 truncate text-appkit-small text-muted"
                    data-testid="preview-card-frame-source"
                  >
                    {source}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
